import { typesByTitle } from '@modules/products/product-types';
import { subtypesById } from '@modules/products/product-subtypes';
import productFamilies from '@modules/products/product-families';
import SessionCache from '@plugins/cache';
import categorizeProducts from '@modules/products/product-sort';
import { defineStore } from 'pinia';
import { useServiceStore } from '@modules/service/m-service';
import { useResourcesStore } from '@modules/resources/m-resources';
import { useProfilesStore } from '@modules/profiles/m-profiles';

const cache = new SessionCache('products');
const cacheKey = 'items';
const byProfileId = (profileId, products) =>
	products.filter(({ profiles }) => profiles && profiles.find(({ id }) => id === profileId));

const checkHasPayrollProduct = (products) =>
	products.some(
		({ productType, productSubtype }) => productType?.id === '19' && productSubtype?.id === '71'
	);

export const useProductsStore = defineStore('m-products', {
	state: () => ({
		lastDetailSelected: null,
		hasPayrollService: false,
		hasCardInstallmentEligibility: null,
		navigationInvestmentProductId: null, // Pension plan, investment or managed rsi portfolio it to navigate from the navigation bar to sso-lighthouse
	}),

	actions: {
		clearProductsCache() {
			cache.clear();
		},
		setHasPayrollService(hasPayroll) {
			this.hasPayrollService = hasPayroll;
		},
		setLastDetailSelected(value) {
			this.lastDetailSelected = value;
		},

		findProductForInvestmentNavigation(products) {
			const filteredProducts = products.filter(({ productFamily }) =>
				['pension-plan', 'investment', 'managed-rsi-portfolio'].includes(productFamily)
			);

			return filteredProducts[0];
		},

		/**
		 *  params.all If true returns all products and does not filter by profile
		 */
		async fetch({ refresh, force, checkPayroll, all } = {}) {
			const profilesStore = useProfilesStore();
			const profileId = useAppStore().isHybridSkyCardManagement
				? null
				: profilesStore.getDefaultProfile?.id;

			if (!refresh && cache.has(cacheKey)) {
				const productsCache = cache.get(cacheKey);
				let finalProductsCache = productsCache;

				if (profileId && !force) {
					finalProductsCache = byProfileId(profileId, productsCache);
				}

				if (checkPayroll) {
					const hasPayroll = checkHasPayrollProduct(finalProductsCache);
					this.setHasPayrollService(hasPayroll);
				}

				return Promise.resolve(finalProductsCache);
			}

			const url = '/products';
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(async ({ data: { data } }) => {
					/* istanbul ignore next */
					if (!data?.length) {
						if (checkPayroll) {
							this.setHasPayrollService(false);
						}
						return [];
					}

					const collection = categorizeProducts(data);

					if (all) return collection;
					const mPortfolioType = typesByTitle['managed-portfolio'];
					const mRSIPortfolioType = typesByTitle['managed-rsi-portfolio'];
					const promises = collection
						.filter(({ productType: { id } }) =>
							[mPortfolioType, mRSIPortfolioType].includes(id)
						)
						.map(async ({ id: productId, profiles }) => {
							const products = await this.getPortfolio({
								productId,
							});
							const normalizedProducts = products.map((item) => ({
								...item,
								parentId: productId,
								productSubtype: { id: `m-${item.productSubtype.id}` },
								relationType: { id: item?.relationType?.id ?? '01' },
								profiles,
							}));

							return categorizeProducts(normalizedProducts);
						});
					const managedProducts = await Promise.all(promises);

					/* istanbul ignore next */
					if (managedProducts?.length) {
						collection.push(...managedProducts.flat());
					}

					await cache.set(cacheKey, collection);

					let finalCollection = collection;

					if (profileId && !useAppStore().isHybridSkyCardManagement) {
						finalCollection = byProfileId(profileId, collection);
					}

					if (checkPayroll) {
						const hasPayroll = checkHasPayrollProduct(finalCollection);
						this.setHasPayrollService(hasPayroll);
					}

					this.navigationInvestmentProductId =
						this.findProductForInvestmentNavigation(finalCollection)?.id;

					return finalCollection;
				});
		},

		getAssistedProducts({ data, source, origin }) {
			const { userId } = data;
			const userUUID = useSecureStore().getUuid;
			return this.fetch({ force: true, all: true }).then((data) => {
				source.postMessage(
					{
						name: 'get-products',
						payload: data,
						userUUID,
						userId,
					},
					origin
				);
			});
		},

		async get(id) {
			let products = cache.get(cacheKey);
			/* istanbul ignore next */
			if (!products?.length) {
				products = await this.fetch();
			}

			/* istanbul ignore next */
			if (id?.includes('/')) {
				const [productId] = id.split('/');
				return products.find(({ id: itemId }) => itemId === productId);
			}

			const product = products.find(({ id: itemId }) => itemId === id);
			/* istanbul ignore next */
			if (!product) {
				return Promise.reject('Product not found');
			}

			return product;
		},

		getCreditors({ productId }) {
			const url = `/products/${productId}/direct-debits/creditors`;
			const method = 'GET';
			const key = `creditors/${productId}`;

			if (cache.has(key)) {
				return new Promise((resolve) => resolve(cache.get(key)));
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => {
					cache.set(key, data);
					return data;
				});
		},

		getCreditCardScps({ refresh, productId, paginationKey }) {
			const url = `/products/${productId}/scps`;
			const method = 'GET';
			const key = `scps/${productId}`;
			const queryParams = { paginationKey };

			if (refresh) {
				SessionCache.clear(key);
			}
			if (cache.has(key) && !paginationKey) {
				return Promise.resolve(cache.get(key));
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => {
					const response = { data: data.data, paging: data.paging };
					if (cache.has(key)) {
						const cacheResult = cache.get(key);
						Object.assign(response.data, cacheResult.data.concat(response.data));
					}
					cache.set(key, response);

					return response;
				});
		},

		getSingleProduct({ productId }) {
			const url = `/products/${productId}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
			});
		},

		getDirectDebits({ paginationKey, productId, creditorId }) {
			const url = `/products/${productId}/direct-debits/creditors/${creditorId}/direct-debits`;
			const method = 'GET';
			const queryParams = {};

			if (paginationKey) {
				Object.assign(queryParams, { paginationKey });
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data);
		},

		getDirectDebitsDetail({ productId, creditorId, directDebitId }) {
			const url = `/products/${productId}/direct-debits/creditors/${creditorId}/direct-debits/${directDebitId}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		deleteDirectDebit({ productId, idDelete }) {
			const url = `/products/${productId}/direct-debits/creditors/${idDelete}`;
			const method = 'DELETE';

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
			});
		},

		getPosition({ productId }) {
			const url = `/products/${productId}/position`;
			const method = 'GET';
			const key = `position/${productId}`;

			if (cache.has(key)) {
				return Promise.resolve(cache.get(key));
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => {
					cache.set(key, data);
					return data;
				});
		},

		getInterveners(productId) {
			const url = `/products/${productId}/interveners`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		selectHoldersCertificate(productId, interveners) {
			const url = `/products/${productId}/holderCertificate`;
			const method = 'POST';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: interveners,
				})
				.then(({ data: { content } }) => content);
		},

		getMovementDetail({ productId, movementId, movementCoreId, typeId }) {
			const url = `/products/${productId}/movements/${movementId}`;
			const method = 'GET';

			const key = `/product/${productId}/debit/${movementId}`;

			if (cache.has(key)) {
				return new Promise((resolve) => resolve(cache.get(key)));
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: {
						movementCoreId,
						typeId,
					},
				})
				.then(({ data }) => {
					cache.set(key, data);
					return cache.get(key);
				})
				.catch(() => {});
		},

		getPortfolio({ productId }) {
			const url = `/products/${productId}/managedProducts`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { data } }) => data);
		},

		getCardCVV({ productId }) {
			const url = `/products/${productId}/cvv`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { cvv } }) => cvv);
		},

		getCardPIN({ productId }) {
			const url = `/products/${productId}/pin`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { pin } }) => pin);
		},

		getCardPAN({ productId }) {
			const url = `/products/${productId}/pan`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { pan } }) => pan);
		},

		byService(byService) {
			const url = '/products';
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams: { byService },
				})
				.then(({ data: { data } }) => {
					/* istanbul ignore next */
					if (!data?.length) {
						return;
					}

					return categorizeProducts(data);
				});
		},

		byCoreType({ byCoreType, byRelationType }) {
			const url = '/products';
			const method = 'GET';
			const contractType = useContractsStore().connectedContract?.natureTypeOwner?.id;

			/*istanbul ignore if*/
			if (contractType === 'J') {
				byRelationType = '01|07';
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams: {
						byCoreType,
						byRelationType,
					},
				})
				.then(({ data: { data } }) => {
					/* istanbul ignore next */
					if (!data?.length) {
						return;
					}

					return categorizeProducts(data);
				});
		},

		getDetails(id) {
			if (id.includes('/')) {
				const resourcesStore = useResourcesStore();
				const parts = id.split('/');
				const [productId, resource, resourceId] = parts;

				return resourcesStore.get({
					resource,
					productId,
					resourceId,
				});
			}

			const key = `detail/${id}`;

			/* istanbul ignore next */
			if (cache.has(key)) {
				return Promise.resolve(cache.get(key));
			}

			const url = `/products/${id}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => {
					cache.set(key, data);

					return data;
				});
		},

		/* istanbul ignore next */
		async getSiblings(productId) {
			const profilesStore = useProfilesStore();
			const profileId = useAppStore().isHybridSkyCardManagement
				? null
				: profilesStore.getDefaultProfile?.id;
			let products = cache.get(cacheKey);

			if (!products?.length) {
				products = await this.fetch();
			}

			if (profileId) {
				products = byProfileId(profileId, products);
			}

			const categorizedProducts = products.reduce((reducer, item) => {
				const productSubtype = subtypesById[item.productSubtype.id];
				const families = Object.entries(productFamilies);
				const familyGroup = families.find(([, group]) => group.includes(productSubtype));

				if (familyGroup) {
					const [familyName] = familyGroup;

					if (!reducer[familyName]) {
						reducer[familyName] = [];
					}

					reducer[familyName].push(item);
				}

				return reducer;
			}, {});
			const [, familyMembers] = Object.entries(categorizedProducts).find(([, group]) =>
				group.find(({ id }) => id === productId)
			);
			return familyMembers;
		},

		getSimulateSettlement(productId) {
			const url = `/products/${productId}/simulate-settlement`;
			const method = 'POST';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		putAlias({ alias, productId }) {
			const url = `/products/${productId}/alias`;
			const method = 'PUT';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: { alias },
				})
				.then(() => {
					const getCache = cache.get(cacheKey);

					getCache?.forEach((item, index) => {
						/* istanbul ignore next */
						if (item.id === productId) {
							getCache[index].alias = alias;
						}
					});
				});
		},

		getProductDocumentXLSX(productId) {
			const url = `/products/${productId}/document`;
			const method = 'GET';
			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { content } }) => content);
		},

		getReceipt({ productId, movementId, query = {}, reportType = 'pdf', fullResponse = false }) {
			const urlDetail = `/products/${productId}/movements/${movementId}/document`;
			const urlList = `/products/${productId}/movements/document`;
			const method = 'GET';
			const url = movementId ? urlDetail : urlList;

			const serviceStore = useServiceStore();

			const request = serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
				queryParams: {
					reportType,
					...query,
				},
			});

			if (fullResponse) {
				return request;
			}

			return request.then(({ data: { content } }) => content);
		},

		checkStatus({ productId, idProcess, signal }) {
			const url = `/products/${productId}/movements/documentValidation/${idProcess}`;
			const method = 'GET';
			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
						signal,
					},
				},
			});
		},

		getCertificate({ productId, movementId, query = {} }) {
			const url = `/products/${productId}/movements/${movementId}/transferCertificate`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams: { ...query },
				})
				.then(({ data: { content } }) => content);
		},

		getDebitCertificate({ productId, movementId, reference }) {
			const url = `/products/${productId}/movements/${movementId}/debitCertificate?reference=${reference}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { content } }) => content);
		},

		getHolderCertificate({ productId }) {
			const url = `/products/${productId}/holderCertificate`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data: { content } }) => content);
		},

		getAccountLimits({ productId }) {
			const url = `/products/${productId}/limits`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		changeAccountLimit({ productId, ...params }) {
			const url = `/products/${productId}/limits`;
			const method = 'PUT';
			const payload = { ...params };

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then((data) => data);
		},

		getCardLimits({ productId }) {
			const url = `/products/${productId}/limits`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		activateCard({ productId }) {
			const url = `/products/${productId}/cardActivation`;
			const method = 'PUT';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((data) => data);
		},

		blockCard({ productId, cardBlockType }) {
			const url = `/products/${productId}/cardBlock`;
			const method = 'PUT';
			const payload = {};

			/* istanbul ignore else */
			if (cardBlockType) {
				Object.assign(payload, { cardBlockType });
			}

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
				payload,
			});
		},

		changeCardLimit({ productId, ...params }) {
			const url = `/products/${productId}/changeCardLimit`;
			const method = 'PUT';
			const payload = { ...params };

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then((data) => data);
		},

		blockCardTemporally({ productId, activate }) {
			const url = `/products/${productId}/temporalyCardBlock`;
			const method = 'PUT';
			const payload = { activate };

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
				payload,
			});
		},

		ssoBolsaCaminos() {
			const url = '/products/sso/bolsacaminos';
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((data) => data);
		},
		ssoBolsaCaminosAssisted(userId) {
			const url = `/products/sso/bolsacaminos/${userId}/profile`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((data) => data);
		},
		modifyBolsaCaminosAssisted({ userId, status }) {
			const url = `/products/sso/bolsacaminos/${userId}/profile`;
			const method = 'PATCH';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: status,
				})
				.then((data) => data);
		},
		bolsaCaminos() {
			const key = 'bolsacaminos';

			if (cache.has(key)) {
				return Promise.resolve(cache.get(key));
			}

			const url = '/products/bolsacaminos';
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((data) => {
					cache.set(key, data);
					return data;
				});
		},
		changeUsageLimit({ productId, ...params }) {
			const url = `/products/${productId}/usagelimit`;
			const method = 'PUT';
			const payload = { ...params };

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
				payload,
			});
		},

		getPaymentMethod({ productId }) {
			const url = `/products/${productId}/paymentmethod`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((data) => data.data);
		},

		updatePaymentMethod({ productId, ...params }) {
			const url = `/products/${productId}/paymentmethod`;
			const method = 'PUT';
			const payload = { ...params };

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then((data) => data);
		},

		getCardInstallmentEligibility() {
			const key = `installmentEligibility`;
			if (cache.has(key)) {
				return Promise.resolve(cache.get(key));
			}

			const url = `/products/cardInstallmentEligibility`;

			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => {
					cache.set(key, data);
					this.hasCardInstallmentEligibility = data.active;
					return data;
				});
		},

		getCardEvents({ productId, paginationKey, dateFrom, dateTo, responseCode }) {
			const url = `/products/${productId}/events`;
			const method = 'GET';
			const queryParams = {};

			if (paginationKey) {
				Object.assign(queryParams, { paginationKey });
			}

			if (dateFrom) {
				Object.assign(queryParams, { dateFrom });
			}

			if (dateTo) {
				Object.assign(queryParams, { dateTo });
			}

			if (responseCode) {
				Object.assign(queryParams, { responseCode });
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data);
		},

		getEventDetail({ productId, eventId }) {
			const url = `/products/${productId}/events/${eventId}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},
		getMediquo({ productId }) {
			const url = `/products/sso/mediquo/${productId}`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		getMediquoAllowList() {
			const url = '/products/mediquoAllowList';
			const method = 'GET';
			const serviceStore = useServiceStore();
			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
			});
		},

		getBalanceCertificate({ productId, date }) {
			const url = `/products/${productId}/balanceCertificate`;
			const method = 'GET';
			const queryParams = {
				date,
			};

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data: { content } }) => content);
		},
	},
});
